import React from 'react';
import { LeftIcon } from '../common/icon';
import { isMobile } from '@/biz/tool';
import { useTranslation } from 'react-i18next';

export const InfluencerAgreement: React.FC<{
  setIsShowAgreement: Function;
}> = ({ setIsShowAgreement }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`bg-white rounded-lg 
      ${isMobile() ? 'h-[60vh]' : 'h-[70vh]'}
    `}
    >
      <div className="h-[40px] relative flex items-center justify-between px-0 py-4 border-b border-gray-200">
        <button
          onClick={() => setIsShowAgreement(false)}
          className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
        >
          <LeftIcon className="w-10 h-10" />
          <span className="font-bold text-[#1c2024]">
            {t('campaign.agreement.back_btn', { defaultValue: 'Back' })}
          </span>
        </button>
        <h1
          className={`text-xl absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-semibold text-gray-800 mb-2 text-nowrap`}
        >
          {isMobile()
            ? t('campaign.agreement.title_mobile', {
                defaultValue: 'Agreement'
              })
            : 'Influencer & Affiliate Partner Agreement'}
        </h1>
      </div>

      <div className="px-2 py-4 text-gray-600 leading-relaxed h-[calc(100%-40px)] overflow-auto text-xs">
        <p className="mb-2 text-xs text-[#337ea9] font-medium">
          {t('campaign.agreement.last_update', {
            defaultValue: 'Last Update: June 13, 2025'
          })}
        </p>
        <p className="mb-4">
          {t('campaign.agreement.intro', {
            defaultValue:
              'This Influencer & Affiliate Partner Agreement (hereinafter referred to as this "Agreement") shall constitute a legally binding electronic agreement between Ahalab.ai Inc. (hereinafter referred to as "Party A") and the Influencer or Affiliate Partner (hereinafter referred to as "Party B") to regulate the rights and obligations of both parties. This Agreement shall become effective immediately upon Party A\'s issuance of a confirmation notice to Party B, either for influencer marketing services or for participation in the affiliate marketing program.'
          })}
        </p>
        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.whereas_title', { defaultValue: 'WHEREAS:' })}
        </h2>
        <p className="mb-2">
          {t('campaign.agreement.party_a_desc', {
            defaultValue:
              'Party A is a technology company specializing in marketing and product promotion services via a unified platform that includes Influencer Marketing, Affiliate Marketing, Cold Email, and AI-powered content/insight support.'
          })}
        </p>
        <p className="mb-2">
          {t('campaign.agreement.party_b_is', {
            defaultValue: 'Party B is either:'
          })}
        </p>
        <ul className="list-disc pl-5 mb-2">
          <li>
            {t('campaign.agreement.party_b_option1', {
              defaultValue:
                'a social media creator with influence on one or more platforms, or'
            })}
          </li>
          <li>
            {t('campaign.agreement.party_b_option2', {
              defaultValue:
                'an affiliate partner capable of promoting third-party products via links, content, or other means to generate measurable results.'
            })}
          </li>
        </ul>
        <p className="mb-4">
          {t('campaign.agreement.both_parties_wish', {
            defaultValue:
              'Both parties wish to establish a collaborative relationship.'
          })}
        </p>
        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article1_title', {
            defaultValue: 'ARTICLE 1 – DEFINITIONS'
          })}
        </h2>

        <p>
          <b>
            {t('campaign.agreement.def_sns_platform', {
              defaultValue: 'SNS Platform Account'
            })}
          </b>
          {': '}
          {t('campaign.agreement.def_sns_platform_desc', {
            defaultValue:
              'Social media accounts used by Party B for influencer marketing activities.'
          })}
        </p>
        <p>
          <b>
            {t('campaign.agreement.def_affiliate_system', {
              defaultValue: 'Affiliate System'
            })}
          </b>
          {': '}
          {t('campaign.agreement.def_affiliate_system_desc', {
            defaultValue:
              "Party A's platform system or designated third-party tracker to manage and record affiliate promotions and conversions."
          })}
        </p>
        <p>
          <b>
            {t('campaign.agreement.def_campaign', { defaultValue: 'Campaign' })}
          </b>
          {': '}
          {t('campaign.agreement.def_campaign_desc', {
            defaultValue:
              'Either a content-based influencer campaign initiated by Party A, or an affiliate offer where Party B is invited to promote with commission terms.'
          })}
        </p>
        <p>
          <b>
            {t('campaign.agreement.def_creative_content', {
              defaultValue: 'Creative Content'
            })}
          </b>
          {': '}
          {t('campaign.agreement.def_creative_content_desc', {
            defaultValue:
              'Content created by Party B for influencer marketing purposes, including but not limited to videos, texts, images, audio, links, and scripts.'
          })}
        </p>
        <p>
          <b>
            {t('campaign.agreement.def_promoted_products', {
              defaultValue: 'Promoted Products'
            })}
          </b>
          {': '}
          {t('campaign.agreement.def_promoted_products_desc', {
            defaultValue:
              'Products or services that Party A has the legal right to promote or authorize others to promote.'
          })}
        </p>
        <p>
          <b>
            {t('campaign.agreement.def_affiliate_link', {
              defaultValue: 'Affiliate Link'
            })}
          </b>
          {': '}
          {t('campaign.agreement.def_affiliate_link_desc', {
            defaultValue:
              'A tracking link provided to Party B for affiliate purposes.'
          })}
        </p>
        <p>
          <b>
            {t('campaign.agreement.def_valid_conversion', {
              defaultValue: 'Valid Conversion'
            })}
          </b>
          {': '}
          {t('campaign.agreement.def_valid_conversion_desc', {
            defaultValue:
              "A transaction or action (e.g., sale, signup) generated via Party B's affiliate activities, subject to validation by Party A or its tracking platform."
          })}
        </p>

        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article2_title', {
            defaultValue: 'ARTICLE 2 – COOPERATION PERIOD'
          })}
        </h2>
        <p className="mb-4">
          {t('campaign.agreement.cooperation_period', {
            defaultValue:
              'This Agreement shall take effect from the confirmation date and remain effective for [six] months, unless otherwise stated. Each campaign or affiliate offer may have a separate validity period.'
          })}
        </p>
        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article3_title', {
            defaultValue: 'ARTICLE 3 – COOPERATION CONTENT'
          })}
        </h2>
        <p className="mb-2">
          {t('campaign.agreement.party_b_may', {
            defaultValue: 'Party B may:'
          })}
        </p>
        <ul className="list-disc pl-5 mb-2">
          <li>
            {t('campaign.agreement.party_b_role1', {
              defaultValue:
                "Participate as an Influencer, creating and publishing Creative Content on SNS Platforms per Party A's brief; and/or"
            })}
          </li>
          <li>
            {t('campaign.agreement.party_b_role2', {
              defaultValue:
                'Participate as an Affiliate Partner, promoting via Affiliate Links to generate Valid Conversions.'
            })}
          </li>
        </ul>
        <p className="mb-4">
          {t('campaign.agreement.specific_details', {
            defaultValue:
              'Specific details will be included in each Campaign brief or offer.'
          })}
        </p>
        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article4_title', {
            defaultValue: 'ARTICLE 4 – NO EMPLOYMENT RELATIONSHIP'
          })}
        </h2>
        <p className="mb-4">
          {t('campaign.agreement.no_employment', {
            defaultValue:
              'Nothing contained within this Agreement shall be construed to form any partnership, joint venture, agency, franchise, or employment relationship. Party B is an independent contractor and will, at all times, act as such.'
          })}
        </p>
        <p className="mb-4">
          {t('campaign.agreement.tax_responsibility', {
            defaultValue:
              'Party B shall be solely responsible for paying all taxes assessed or imposed on it under applicable laws arising from or related to this Agreement. Party A shall not withhold any taxes from the fees payable to Party B, unless otherwise expressly agreed.'
          })}
        </p>
        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article5_title', {
            defaultValue: "ARTICLE 5 – PARTY A'S RIGHTS AND OBLIGATIONS"
          })}
        </h2>

        <ul className="list-disc pl-8 mb-2">
          <li>
            {t('campaign.agreement.ownership_title', {
              defaultValue: 'Ownership of Intellectual Property'
            })}
            <p>
              {t('campaign.agreement.ownership_desc', {
                defaultValue:
                  'All copyrights, intellectual property rights, and any other rights related to the materials (including but not limited to text, images, music, audio, video, etc.) provided by Party A under this Agreement shall be the sole and exclusive property of Party A or its product suppliers/brands.'
              })}
            </p>
          </li>
          <li>
            {t('campaign.agreement.content_ownership_title', {
              defaultValue: 'Content Ownership and License'
            })}
            {t('campaign.agreement.content_ownership_intro', {
              defaultValue:
                ' By providing the Creative Content to Party A, Party B acknowledges that:'
            })}
            <ul className="list-disc pl-8 mb-2">
              <li>
                {t('campaign.agreement.content_ownership_point1', {
                  defaultValue:
                    "Party A holds the intellectual property rights to the Creative Content, and Party A is granted an irrevocable, worldwide license to use Party B's image, name, voice, and other rights related to Party B's personality included in the Creative Content."
                })}
              </li>
              <li>
                {t('campaign.agreement.content_ownership_point2', {
                  defaultValue:
                    'Party A and the product owner are granted an irrevocable, worldwide license to reproduce, display, modify, distribute, and create derivative works of the Creative Content for commercial purposes such as marketing and advertising.'
                })}
              </li>
            </ul>
          </li>
          <li>
            {t('campaign.agreement.approval_title', {
              defaultValue: 'Approval and Modifications'
            })}
            {t('campaign.agreement.approval_desc', {
              defaultValue:
                "During the cooperation period, Party A has the right to provide suggestions or guidance regarding the Creative Content, including content planning, copywriting, and video editing support. Party A may also request certain formatting or editing changes (e.g., opening/closing credits, subtitles). If Party B's content does not meet Party A's requirements, Party A reserves the right to:"
            })}
            <ul className="list-disc pl-8 mb-2">
              <li>
                {t('campaign.agreement.approval_point1', {
                  defaultValue: 'Not publish or launch the content,'
                })}
              </li>
              <li>
                {t('campaign.agreement.approval_point2', {
                  defaultValue:
                    'Consider the cooperation incomplete, which may lead to cancellation of the final payment and a refund request for any initial payments made.'
                })}
              </li>
            </ul>
          </li>
          <li>
            {t('campaign.agreement.branding_title', {
              defaultValue: 'Inclusion of Product Supplier Branding'
            })}
            {t('campaign.agreement.branding_desc', {
              defaultValue:
                'Party A may require Party B to display branding from the Product Supplier in the Creative Content (such as company logos).'
            })}
          </li>
          <li>
            {t('campaign.agreement.negative_publicity_title', {
              defaultValue: 'Negative Publicity'
            })}
            {t('campaign.agreement.negative_publicity_desc', {
              defaultValue:
                'If any negative publicity or unfavorable news about Party B appears on any SNS platform during the cooperation period, Party B shall take reasonable steps to mitigate the negative impact.'
            })}
          </li>
          <li>
            {t('campaign.agreement.delays_title', {
              defaultValue: 'Delays or Postponements'
            })}
            {t('campaign.agreement.delays_desc', {
              defaultValue:
                'Party A has the right to adjust or postpone the launch time of the Creative Content due to platform requirements. If Party B fails to make the necessary adjustments within the reserved time, Party B will be responsible for the related costs and consequences.'
            })}
          </li>
          <li>
            {t('campaign.agreement.payment_title', {
              defaultValue: 'Payment for Completed Content'
            })}
            {t('campaign.agreement.payment_desc', {
              defaultValue:
                "If Party B's Creative Content meets the Campaign requirements, Party A shall settle and pay Party B in a timely manner as outlined in the Campaign's terms."
            })}
          </li>
          <li>
            {t('campaign.agreement.content_retention_title', {
              defaultValue: 'Content Retention'
            })}
            {t('campaign.agreement.content_retention_desc', {
              defaultValue:
                'Party B shall not voluntarily remove the published content unless due to force majeure. If content is taken down due to a force majeure event, Party A may request Party B to repost it.'
            })}
          </li>
          <li>
            {t('campaign.agreement.non_performance_title', {
              defaultValue: 'Non-Performance of Agreement'
            })}
            {t('campaign.agreement.non_performance_desc', {
              defaultValue:
                'If Party B fails to meet deadlines or deliver content as agreed (e.g., missed posts, failure to submit materials), Party A may choose to adjust the post launch time or cancel the promotion. In the event of cancellation, Party B must return any advance payment and compensate Party A for any resulting losses.'
            })}
          </li>
          <li>
            {t('campaign.agreement.fraudulent_activity_title', {
              defaultValue: 'Fraudulent Activity'
            })}
            {t('campaign.agreement.fraudulent_activity_desc', {
              defaultValue:
                "If Party B's account is involved in fraudulent activity (e.g., traffic manipulation, plagiarism), Party A reserves the right to immediately terminate the cooperation and withhold any further payments. Party B must return any payments already made and may be subject to legal action for damages. Party A may also claim up to five times the amount of actual losses as liquidated damages, where permitted by law."
            })}
          </li>
        </ul>

        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article6_title', {
            defaultValue: "ARTICLE 6 – PARTY B'S RIGHTS AND OBLIGATIONS"
          })}
        </h2>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>
            {t('campaign.agreement.party_b_right1', {
              defaultValue:
                'Party B has the right to timely receive the remuneration in accordance with Article 7 of this Agreement.'
            })}
          </li>
          <li>
            <b>
              {t('campaign.agreement.party_b_obligation1', {
                defaultValue:
                  "Party B shall create content in accordance with the Campaign requirements specified in Article 3 and improve it based on the suggestions or requirements of Party A. Party B shall also upload the script or draft to the Designated Platform within the agreed time and send the related link to Party A for confirmation. If Party B fails to upload the content as per the platform's requirements and within the designated timeframe, Party B shall be liable for any late fees as stipulated by the platform. If the content is not uploaded by the platform's final deadline, the cooperation shall be deemed canceled, and Party A shall be entitled to request a refund of any payments made by the platform. Furthermore, Party B shall be responsible for all prior costs incurred, including but not limited to shipping fees, handling fees, and other related expenses."
              })}
            </b>
          </li>
          <li>
            <b>
              {t('campaign.agreement.party_b_obligation2', {
                defaultValue:
                  "Party B confirms and guarantees the Creative Content it provided, delivered, or transmitted, shall comply with the applicable local laws, regulations, and policies and shall not contain any illegal content. Party B guarantees that the Creative Content does not infringe the rights of third parties, including without limitation, intellectual property rights, trade secrets, rights and interests relating to personality and rights of privacy. Party B guarantees that the Creative Content has not been copied or plagiarized from a third party's work, including but not limited to, not directly copying a third party's video script, not plagiarizing a third party's video's script, characters and settings, plots, lines, flow design and sequences, etc., and not using without authorization, any third party's copyrighted text, character, pictures, photographs, music, and any other copyrightable content or elements."
              })}
            </b>
          </li>
          <li>
            <b>
              {t('campaign.agreement.party_b_obligation3', {
                defaultValue:
                  'Party B undertakes to assume all responsibilities for any loss, damage, or obligations caused by the violation of laws or regulations by the Creative Content, or the infringement of third-party rights, or any disputes arising therefrom. For any losses caused to Party A, Party B shall make full compensation to Party A and ensure Party A takes no responsibility and assumes no liability arising from or relating to the Creative Content.'
              })}
            </b>
          </li>
          <li>
            {t('campaign.agreement.party_b_obligation4', {
              defaultValue:
                "Party B shall ensure compliance with applicable laws and regulations, as well as Party A's relevant requirements, and refrain from engaging in any illegal or criminal activities. Party B shall strictly exercise self-discipline, ensure no serious bad habits, abide by professional ethics, public order and good customs, and improve moral cultivation."
            })}
          </li>
          <li>
            {t('campaign.agreement.party_b_obligation5', {
              defaultValue:
                'Party B has full civil capacity, legal capacity, and sufficient authorization to enter into and perform this agreement in accordance with its contents. Party B agrees with Party A on the content of this Agreement and voluntarily signs and performs this Agreement.'
            })}
          </li>
        </ul>
        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article7_title', {
            defaultValue: 'ARTICLE 7 – FEES AND PAYMENT'
          })}
        </h2>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>
            {t('campaign.agreement.payment_rule1', {
              defaultValue:
                'Influencer payments shall be based on Campaign terms.'
            })}
          </li>
          <li>
            {t('campaign.agreement.payment_rule2', {
              defaultValue:
                'Affiliate commissions shall be based on Valid Conversions tracked and confirmed.'
            })}
          </li>
          <li>
            {t('campaign.agreement.payment_rule3', {
              defaultValue:
                'Taxes shall be borne respectively by each party as per law.'
            })}
          </li>
          <li>
            {t('campaign.agreement.payment_rule4', {
              defaultValue:
                "If Party B fails to meet the content quality or delivery requirements after multiple revision requests in accordance with the platform's standard workflow, or explicitly refuses to make the required modifications, or fails to submit content or revisions by the deadlines specified in the campaign brief or as reasonably requested by Party A, Party B shall forfeit the right to receive any compensation or commission for that specific campaign. Furthermore, all Creative Content already produced in relation to that campaign shall become the property of the advertiser, and Party A and its product partners shall have full rights to use, modify, or discard such content at their sole discretion."
            })}
          </li>
        </ul>
        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article8_title', {
            defaultValue: 'ARTICLE 8 – CONFIDENTIALITY OBLIGATION'
          })}
        </h2>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>
            {t('campaign.agreement.confidentiality_rule1', {
              defaultValue:
                'The content of this Agreement, as well as all information known to the other party, including but not limited to personnel, financial data, partners, etc., during the signing and performance of this Agreement, is considered confidential. The receiving party shall use the Confidential Information only for the purpose of this Agreement, and shall not use, disclose, copy or replicate all or part of the Confidential Information for purposes other than the performance of the Services in this Agreement. The confidentiality period starts from the effective date of this Agreement and ends on the date when the Confidential Information becomes public information (that is, disclosed to or becomes known to the public). The confidentiality obligation shall not be terminated by the suspension, termination or discharge of this Agreement.'
            })}
          </li>
          <li>
            {t('campaign.agreement.confidentiality_rule2_intro', {
              defaultValue:
                'The confidentiality obligations under this Agreement do not apply to any information that:'
            })}
            <ul className="list-disc pl-8 mb-2">
              <li>
                {t('campaign.agreement.confidentiality_rule2_point1', {
                  defaultValue:
                    'is lawfully in possession of the Receiving Party prior to receipt from the Disclosing Party;'
                })}
              </li>
              <li>
                {t('campaign.agreement.confidentiality_rule2_point2', {
                  defaultValue:
                    'is or becomes publicly known other than as a result of a breach of this Agreement;'
                })}
              </li>
              <li>
                {t('campaign.agreement.confidentiality_rule2_point3', {
                  defaultValue:
                    'is received from a third party without breach of any other relevant confidentiality obligation binding on such third party;'
                })}
              </li>
              <li>
                {t('campaign.agreement.confidentiality_rule2_point4', {
                  defaultValue:
                    'is disclosed with the consent of the Disclosing Party.'
                })}
              </li>
            </ul>
          </li>
          <li>
            {t('campaign.agreement.confidentiality_rule3', {
              defaultValue:
                'If Party B violates the provisions of this Clause and causes Party A to suffer losses, Party B shall pay Party A corresponding compensation.'
            })}
          </li>
        </ul>
        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article9_title', {
            defaultValue: 'ARTICLE 9 – LIABILITY FOR BREACH'
          })}
        </h2>
        <p className="mb-4">
          {t('campaign.agreement.breach_liability', {
            defaultValue:
              "In the event of a breach of this Agreement, without prejudice to the rights of the non-defaulting Party set out in other provisions of this Agreement, the defaulting Party shall be liable to compensate the non-defaulting Party for any losses, damages, costs, expenses (including but not limited to reasonable attorney's fee), liabilities or claims that the non-defaulting Party may suffer as a result of such breach."
          })}
        </p>
        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article10_title', {
            defaultValue: 'ARTICLE 10 – TERMINATION'
          })}
        </h2>
        <ul className="list-disc pl-8 mb-2">
          <li>
            {t('campaign.agreement.termination_rule1', {
              defaultValue:
                'This Agreement may be changed or terminated in advance by consensus between Party A and Party B.'
            })}
          </li>
          <li>
            <b>
              {t('campaign.agreement.termination_for_cause_title', {
                defaultValue: 'Termination for Cause'
              })}
            </b>
            <p>
              <b>
                {t('campaign.agreement.termination_for_cause_intro', {
                  defaultValue:
                    'This Agreement shall automatically terminate, without any obligation of Party A to pay fees or compensation, if Party B engages in any of the following acts. Party A reserves the right to pursue legal remedies, including but not limited to a full refund of any payments made, (including but not limited to deposits, all shipping fees, or any other related costs) and liquidated damages of up to five (5) times the actual losses incurred (to the extent permitted by applicable law):'
                })}
              </b>
            </p>
            <ul className="list-disc pl-8">
              <li>
                <b>
                  {t('campaign.agreement.termination_cause1', {
                    defaultValue:
                      'Party B fails to fulfill obligations within the agreed timeline set by the platform;'
                  })}
                </b>
              </li>
              <li>
                <b>
                  {t('campaign.agreement.termination_cause2', {
                    defaultValue:
                      'Party B breaches the agreement after accepting the cooperation invitation;'
                  })}
                </b>
              </li>
              <li>
                <b>
                  {t('campaign.agreement.termination_cause3', {
                    defaultValue:
                      "The submitted content is perfunctory or entirely generated by AI, or Party B does not modify or adjust it according to Party A's requirements after Party A's requests for correction and adjustment, Party A considers that Party B is not suitable to continue to perform this Agreement."
                  })}
                </b>
              </li>
              <li>
                <b>
                  {t('campaign.agreement.termination_cause4', {
                    defaultValue:
                      "Party B's account engages in fraudulent behavior such as fake traffic or data manipulation;"
                  })}
                </b>
              </li>
              <li>
                <b>
                  {t('campaign.agreement.termination_cause5', {
                    defaultValue:
                      "At any time between the signing date of this Agreement and the Posting or Launching date of the Creative Content (including that date), the SNS Platform Account is obviously abnormal, blocked, or the exposure of which is restricted by the Platform due to Party B's reason."
                  })}
                </b>
              </li>
            </ul>
          </li>
          <li>
            {t('campaign.agreement.termination_without_liability_title', {
              defaultValue:
                'Termination Without Liability (Force Majeure and Other Events)'
            })}
            <p>
              {t('campaign.agreement.termination_without_liability_intro', {
                defaultValue:
                  'This Agreement shall also automatically terminate under the following circumstances. In such cases, Party A shall not be required to pay any fees, and Party B shall not be deemed in breach of this Agreement:'
              })}
            </p>
            <ul className="list-disc pl-8">
              <li>
                {t('campaign.agreement.termination_without_liability1', {
                  defaultValue:
                    "Occurrence of a force majeure event, including but not limited to natural disasters, war, pandemic, government restrictions, or other unforeseeable events beyond Party B's control that prevent fulfillment of obligations;"
                })}
              </li>
              <li>
                {t('campaign.agreement.termination_without_liability2', {
                  defaultValue:
                    'Party B suffers a serious illness, injury, or other personal emergencies that reasonably prevent them from performing under this Agreement;'
                })}
              </li>
              <li>
                {t('campaign.agreement.termination_without_liability3', {
                  defaultValue:
                    "Party B's social media account is permanently banned, disabled, or suspended in a way that prevents campaign fulfillment;"
                })}
              </li>
              <li>
                {t('campaign.agreement.termination_without_liability4', {
                  defaultValue:
                    'Party B is unable to fulfill their obligations due to legal, regulatory, or policy-related restrictions.'
                })}
              </li>
            </ul>
          </li>
        </ul>
        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article11_title', {
            defaultValue: 'ARTICLE 11 – LAW AND DISPUTE RESOLUTION'
          })}
        </h2>
        <p className="mb-4">
          {t('campaign.agreement.law_and_dispute', {
            defaultValue:
              "This Agreement shall be governed by U.S. law. Disputes shall be resolved in Party A's jurisdiction."
          })}
        </p>
        <h2 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
          {t('campaign.agreement.article12_title', {
            defaultValue: 'ARTICLE 12 – MISCELLANEOUS'
          })}
        </h2>
        <p className="mb-4">
          {t('campaign.agreement.miscellaneous', {
            defaultValue:
              'Matters not covered shall be supplemented by separate written agreements. If any clause is deemed invalid, other clauses shall remain effective.'
          })}
        </p>
      </div>
    </div>
  );
};
